// Story 6.6 (ADO-882, AC1/AC3/AC4) — "Change password" affordance on the
// AUTHENTICATED account surface (FR3). The authenticated-session twin of
// `ForgotPassword.tsx`, with three deliberate differences:
//   • NO email field — the email is known from the session (claims), so the user
//     only clicks a single "Change password" action (AC1; ADR-003; epic #876
//     comment #6998442: the Auth0-hosted page shows new-password fields only).
//   • OUTCOME-AWARE — unlike the enumeration-safe logged-out reset, an initiation
//     failure is surfaced as an RFC 7807-consistent, presentational message (AC3).
//   • The application renders NO password field in ANY state — the credential is
//     entered only on the Auth0-hosted page and never transits the app (AC4).
//
// a11y (NFR24–26): the heading receives focus on mount and on the confirmation
// transition; the confirmation is announced via role="status"; the failure via
// role="alert". A synchronous in-flight guard prevents a double-submit.

import { useEffect, useRef, useState } from 'react';
import { Button } from '../design-system';

export interface ChangePasswordProps {
  /**
   * Trigger the Auth0-hosted change-password flow for the authenticated user.
   * OUTCOME-AWARE: rejects on an initiation failure (AC3). Null when no
   * client/config exists yet — treated as an initiation failure (no false success).
   */
  onRequestChange: (() => Promise<void>) | null;
}

type ChangeStatus = 'idle' | 'sent' | 'error';

export function ChangePassword({ onRequestChange }: ChangePasswordProps) {
  const [status, setStatus] = useState<ChangeStatus>('idle');
  const headingRef = useRef<HTMLHeadingElement>(null);
  // Synchronous guard: the outcome is only known after the awaited send, so we
  // cannot flip the view optimistically (as the logged-out reset does). This ref
  // blocks a second click while the first send is in flight; it is released when
  // the send settles so an error state stays retryable.
  const inFlight = useRef(false);

  // Move focus to the confirmation heading ONLY when the view switches to the
  // 'sent' state, so screen-reader / keyboard users land on the new content.
  // Do NOT steal focus on `idle` mount: unlike the full-page ForgotPassword this
  // was cloned from, ChangePassword renders inline in the authenticated shell, so
  // grabbing focus on mount would yank the user past the surrounding page content.
  // Do NOT steal focus on the error transition either: the failure is announced via
  // the role="alert" region and the user must stay on the button to retry.
  useEffect(() => {
    if (status === 'sent') {
      headingRef.current?.focus();
    }
  }, [status]);

  if (status === 'sent') {
    return (
      <section aria-labelledby="change-password-confirm-heading">
        <h2 id="change-password-confirm-heading" tabIndex={-1} ref={headingRef}>
          Check your email
        </h2>
        {/* AC3 confirmation copy (verbatim). Announced politely to assistive tech. */}
        <p role="status">We&apos;ve emailed you a link to change your password.</p>
      </section>
    );
  }

  return (
    <section aria-labelledby="change-password-heading">
      <h2 id="change-password-heading" tabIndex={-1} ref={headingRef}>
        Change password
      </h2>
      <p>
        We&apos;ll email you a secure link to change your password. You&apos;ll set the new password
        on a page hosted by our identity provider.
      </p>
      <Button
        type="button"
        onClick={() => {
          if (inFlight.current) return;
          inFlight.current = true;
          void (async () => {
            try {
              // A null action means the flow cannot be started — surface the AC3
              // failure rather than a false confirmation.
              if (onRequestChange === null) {
                throw new Error('Password change is unavailable.');
              }
              await onRequestChange();
              setStatus('sent');
            } catch {
              // AC3 — presentational, RFC 7807-consistent failure (title + detail).
              // Never surface a raw error/stack/token/policy internal (AC4).
              setStatus('error');
            } finally {
              inFlight.current = false;
            }
          })();
        }}
      >
        Email me a change-password link
      </Button>
      {status === 'error' && (
        // AC3 — the platform's RFC 7807 vocabulary (Story 5.7) rendered
        // presentationally: a short title + a client-safe detail. The action stays
        // available above for a retry (recoverable, not a terminal state).
        <p role="alert">
          <strong>Couldn&apos;t start password change.</strong> We couldn&apos;t start the
          password-change process. Please try again.
        </p>
      )}
    </section>
  );
}

export default ChangePassword;
